import React from 'react'
import { getJiraAttachmentId } from 'sidecar-utilities'

import {
  CustomReactRendererProps,
  Mark,
  Node,
  RendererOptions,
} from './AtlassianRichTextRenderer.types'

/**
 * Rich text renderer to replace @atlaskit/renderer.
 *
 * @param {Document} document - The rich text document in JSON format.
 * @param {boolean} [hideAttachmentLinks] - When true, links pointing at Jira's
 *   REST attachment endpoints are not rendered. Those URLs require Jira
 *   authentication, so they are dead ends for anyone outside Jira. Consumers
 *   that hide them are expected to offer their own way to open the attachment.
 * @param {Object} node - The node to render.
 * @param {string} node.type - The type of the node.
 * @param {Array} [node.content] - The content of the node, which may include
 *   other nodes.
 * @param {Object} [node.attrs] - Additional attributes of the node.
 * @param {string} [node.attrs.level] - The level of the heading node.
 * @param {string} [node.attrs.text] - The text content for emoji, mention, or
 *   status nodes.
 * @param {string} [node.attrs.id] - The ID for mention nodes.
 * @param {string} [node.attrs.timestamp] - The timestamp for date nodes.
 * @param {string} [node.attrs.color] - The color for status nodes.
 * @param {string} [node.text] - The text content for unknown nodes.
 * @returns {React.ReactNode | null} The rendered node or null if the node type
 *   is excluded.
 */
export const AtlassianRichTextRenderer = ({
  document,
  hideAttachmentLinks = false,
}: CustomReactRendererProps) => {
  const { content } = document
  const renderNode = _createNodeRenderer({ hideAttachmentLinks })
  return <>{content.map(renderNode)}</>
}

const _excludedNodes = [
  'mediaSingle',
  'mediaInline',
  'codeBlock',
  'mediaGroup',
  'media',
  'blockquote',
  'panel',
  'rule',
  'decisionList',
  'decisionItem',
  'taskList',
  'taskItem',
  'table',
  'tableRow',
  'tableCell',
  'tableHeader',
  'bodiedExtension',
  'layoutSection',
  'layoutColumn',
]

// Used to decide whether a paragraph still says anything once its attachment
// links have been removed. Letters and numbers in any alphabet count as words,
// punctuation does not.
const _wordCharacter = /[\p{L}\p{N}]/u

/**
 * Builds the node renderer for a single document, closing over the render
 * options so they don't have to be threaded through every recursive call.
 *
 * We had to use index because it wasn't easy to figure out what could be used
 * as a key. Not ideal, but open to other solutions.
 *
 * @param {Object} options The render options.
 * @param {boolean} options.hideAttachmentLinks Whether to drop Jira attachment
 *   links.
 * @returns {Function} A function that renders a node, or null if the node type
 *   is excluded.
 */
function _createNodeRenderer({ hideAttachmentLinks }: RendererOptions) {
  /**
   * Whether this node is a link to a Jira attachment that we've been asked to
   * hide.
   */
  const isHiddenAttachmentLink = (node: Node): boolean =>
    hideAttachmentLinks &&
    !!node.marks?.some(
      mark => mark.type === 'link' && !!getJiraAttachmentId(mark.attrs?.href),
    )

  /**
   * Renders a paragraph, dropping hidden attachment links. Member Care wraps
   * attachment filenames in brackets, so a paragraph left with nothing but
   * punctuation is dropped entirely rather than rendered as "[]".
   */
  const renderParagraph = (node: Node, index: number): React.ReactNode => {
    const children = node.content || []
    const visibleChildren = children.filter(
      child => !isHiddenAttachmentLink(child),
    )
    const removedAnyLinks = visibleChildren.length < children.length
    const hasWords = visibleChildren.some(child =>
      _wordCharacter.test(child.text || ''),
    )

    if (removedAnyLinks && !hasWords) return null

    return (
      <p key={index}>
        {visibleChildren.length ? visibleChildren.map(renderNode) : null}
      </p>
    )
  }

  /**
   * Renders a text node with its marks applied.
   *
   * Marks compose, so each one wraps the result of the last rather than
   * repeating the text.
   */
  const renderTextWithMarks = (node: Node, index: number): React.ReactNode => {
    if (isHiddenAttachmentLink(node)) return null
    if (!node.marks?.length) return <span key={index}>{node.text}</span>

    return node.marks.reduce<React.ReactNode>(
      (children, mark) => _wrapWithMark(mark, children),
      node.text,
    )
  }

  const renderNode = (node: Node, index: number): React.ReactNode | null => {
    if (_excludedNodes.includes(node.type)) {
      return null // Skip excluded node types
    }

    switch (node.type) {
      case 'paragraph':
        return renderParagraph(node, index)

      case 'text':
        return (
          <React.Fragment key={index}>
            {renderTextWithMarks(node, index)}
          </React.Fragment>
        )

      case 'heading':
        return React.createElement(
          `h${node.attrs?.level}`,
          { key: index },
          node.content?.map(renderNode),
        )

      case 'bulletList':
        return (
          <ul key={index} className="sh-ul">
            {node.content?.map(renderNode)}
          </ul>
        )

      case 'orderedList':
        return (
          <ol key={index} className="sh-ol">
            {node.content?.map(renderNode)}
          </ol>
        )

      case 'listItem':
        return <li key={index}>{node.content?.map(renderNode)}</li>

      case 'emoji':
        return <span key={index}>{node.attrs?.text}</span>

      case 'mention':
        return (
          <a key={index} href={`#mention-${node.attrs?.id}`}>
            {node.attrs?.text}
          </a>
        )

      case 'date':
        return (
          <time key={index}>
            {new Date(parseInt(node.attrs?.timestamp || '', 10)).toDateString()}
          </time>
        )

      case 'status':
        return (
          <span key={index} style={{ color: node.attrs?.color }}>
            {node.attrs?.text}
          </span>
        )

      case 'hardBreak':
        return <br key={index} />

      case 'inlineCard':
        return _renderInlineCard(node, index, hideAttachmentLinks)

      default:
        return <span key={index}>{node.text || 'Unknown node'}</span>
    }
  }

  return renderNode
}

/**
 * Helper function to wrap already-rendered content in a single inline mark
 * (like bold, italic, etc.).
 *
 * @param {Object} mark The mark to apply.
 * @param {React.ReactNode} children The content the mark applies to.
 * @returns {React.ReactNode} The wrapped content.
 */
function _wrapWithMark(mark: Mark, children: React.ReactNode): React.ReactNode {
  switch (mark.type) {
    case 'strong':
      return <strong>{children}</strong>

    case 'em':
      return <em>{children}</em>

    case 'underline':
      return <u>{children}</u>

    case 'link':
      return (
        <a href={mark.attrs?.href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      )

    case 'strike':
      return <s>{children}</s>

    case 'subsup':
      return mark.attrs?.type === 'sub' ? (
        <sub>{children}</sub>
      ) : (
        <sup>{children}</sup>
      )

    case 'textColor':
      return <span style={{ color: mark.attrs?.color }}>{children}</span>

    case 'backgroundColor':
      return (
        <span style={{ backgroundColor: mark.attrs?.color }}>{children}</span>
      )

    default:
      return <span>{children}</span>
  }
}

/**
 * Helper function to render an inline card, which Jira also uses for bare
 * links.
 *
 * @param {Object} node The node to render.
 * @param {number} index Used for keys.
 * @param {boolean} hideAttachmentLinks Whether to drop Jira attachment links.
 * @returns {React.ReactNode | null} The rendered link, or null when it points
 *   at a hidden Jira attachment.
 */
function _renderInlineCard(
  node: Node,
  index: number,
  hideAttachmentLinks: boolean,
): React.ReactNode | null {
  if (hideAttachmentLinks && getJiraAttachmentId(node.attrs?.url)) return null

  return (
    <a
      key={index}
      href={node.attrs?.url}
      target="_blank"
      rel="noopener noreferrer"
    >
      {node.attrs?.url}
    </a>
  )
}
